import React from 'react';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { BLUE_RAMP, CHART_COLORS, CHART_INK } from './common';

interface PlayerActivityChartProps {
  data: Array<Record<string, number | string>>;
  /**
   * `activity` pairs engagement with acquisition (active players vs signups);
   * `growth` pairs the population curve with the signups feeding it.
   */
  variant?: 'activity' | 'growth';
}

const formatCount = (value: number) =>
  Number.isFinite(value) ? Math.round(value).toLocaleString('en-US') : '0';

const axisTick = { fontSize: 11, fill: CHART_INK.muted } as const;

/**
 * Player population over time.
 *
 * New signups always get their own right-hand axis. They are an order of
 * magnitude smaller than active players and three orders smaller than the total
 * population — on a shared axis the bars flatten to invisible slivers, which is
 * exactly the metric a growth-focused reader came for.
 *
 * The line axis auto-scales rather than starting at zero, so a curve that moves
 * a fraction of a percent within one day still has visible shape. That is safe
 * for a line; it is why the population is not drawn as a filled area, where a
 * truncated axis would overstate the movement.
 */
const PlayerActivityChart: React.FC<PlayerActivityChartProps> = ({
  data,
  variant = 'activity',
}) => {
  const isGrowth = variant === 'growth';
  const lineKey = isGrowth ? 'totalPlayers' : 'activePlayers';
  const lineName = isGrowth ? 'Total players' : 'Active players';

  return (
    <ResponsiveContainer width="100%" height="100%">
      <ComposedChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={CHART_INK.grid} vertical={false} />
        <XAxis
          dataKey="label"
          tick={axisTick}
          tickLine={false}
          axisLine={false}
          minTickGap={16}
        />
        <YAxis
          yAxisId="line"
          domain={isGrowth ? ['dataMin', 'dataMax'] : [0, 'auto']}
          tickFormatter={formatCount}
          tick={axisTick}
          tickLine={false}
          axisLine={false}
          width={56}
          allowDecimals={false}
        />
        <YAxis
          yAxisId="new"
          orientation="right"
          tickFormatter={formatCount}
          tick={axisTick}
          tickLine={false}
          axisLine={false}
          width={44}
          allowDecimals={false}
        />
        <Tooltip
          formatter={(value: number, name: string) => [formatCount(value), name]}
          contentStyle={{ borderRadius: 10, border: `1px solid ${CHART_INK.grid}`, fontSize: 12 }}
          cursor={{ fill: 'rgba(37, 99, 235, 0.06)' }}
        />
        <Legend wrapperStyle={{ fontSize: 12 }} iconType="circle" iconSize={8} />
        <Bar
          yAxisId="new"
          dataKey="newPlayers"
          name="New players"
          fill={CHART_COLORS.credits}
          radius={[3, 3, 0, 0]}
          maxBarSize={28}
          isAnimationActive={false}
        />
        <Line
          yAxisId="line"
          type="monotone"
          dataKey={lineKey}
          name={lineName}
          stroke={BLUE_RAMP[0]}
          strokeWidth={2}
          dot={false}
          activeDot={{ r: 4, strokeWidth: 2, stroke: '#ffffff' }}
          isAnimationActive={false}
        />
      </ComposedChart>
    </ResponsiveContainer>
  );
};

export default PlayerActivityChart;
